import { fn } from '../utils';
import { CompatibilityService } from "../services/compatibilityservice";
import { IFDRW_Workflow_Setup_WS, IFDRW_Workflow_Users } from './rwfpages.generated';
import { TNavUrlAccess, INavUrlInfo, ResolveNavUrl, BuildNavUrl, NavUrlService } from "../services/navurlservice";
import { NavClientService, TDefinedPageName } from "../navdata";
import { RwfInvoiceDataService } from './rwfinvoicedata';
import { FwfDataService } from './fwfdata.service';
import { IAutoResolvable } from '../services/autoresolveservice';
import { RwfUserInteraction, ValidateCompanyConfig } from './rwfuserinteraction';
import { RwfAppSettings } from './rwf.appsettings';
import { IRwfBootstrapParameters, RwfBootstrap } from './rwf.bootsrap';
import { RwfApplicationService } from '@common/rwf/rwf.application';
import { AsyncInfomaClientIntegration } from '@common/infomaclient';
import { RedirectToInfomaOnlineLogin } from './rwf.infomaonline';

/** Calls to backend for RWF */
export class RwfBackend {
  constructor(
    /** Injection Root */
    private readonly $c: CompatibilityService,

    /** Explicit Nav Url Configuration, fallback is default of NavClientService */
    private readonly explicitNavUrl: TNavUrlAccess = undefined
  ) {
    this.NavClient = $c.resolve("NavClient");
    this.NavUrl = $c.resolve("NavUrl");
  }
  private readonly NavClient: NavClientService;

  private readonly NavUrl: NavUrlService;

  /** Read RWF Settings from NAV Backend, Throwing 'Graceful' user messages on failure, throw error if none found */
  LoadWorkflowSetup(explicitNavUrl?: TNavUrlAccess) {
    return this.resolveNavUrl(explicitNavUrl).then(urlInfo => {
      return this.NavClient.ReadMultiple<"FDRW_Workflow_Setup_WS">({
        PageName: "FDRW_Workflow_Setup_WS", PageCount: 2, Constraints: [
          { FieldName: "Primary_Key", Value: "=''" } // Primary_Key = '' by convention : is the active one
        ]
      }, urlInfo.FullUrl).then(s => {
        if (s.Data.length == 0)
          throw fn.error.ExplicitError(`Für Mandant "${urlInfo.Mandant}" ist kein Workflow konfiguriert!`);
        else if (s.Data.length > 1)
          throw fn.error.ExplicitError(`Mehrere RWF3-Einstellungen gefunden!`);
        return s.Data[0];
      }).then(f => f, err => {
        fn.error.ThrowIfExplicit(err);
        // MSQQQ: FullUrl?!? will throw fatal error if not with comnpany (!?!)
        const GetCompanies = this.NavClient.GetCompanies(urlInfo.FullUrl);
        return GetCompanies.then((companies) => {
          validateCompany(urlInfo.Mandant, companies);
          throw err;
        }, (_err) => {
          // Prefer base error, throw specfic error only if companies not available!
          return GetCompanies.then(() => {
            throw new Error(`Die Workflowkonfiguration für Mandant "${urlInfo.Mandant}" konnte nicht bestimmt werden. \n:Details:` + fn.resolveToError(err));
          });
        });
      });
    });
  }

  /** Read 'own' Workflow User Information for current User if available */
  LoadWorkflowUser(explicitNavUrl?: TNavUrlAccess, throwIfNoneAvailable?: boolean) {
    return this.resolveNavUrl(explicitNavUrl, true).then(navUrl => {
      return this.NavClient.ReadMultiple<"FDRW_WF_User">({
        PageName: "FDRW_WF_User",
        PageCount: 0, Constraints: []
      }, navUrl, true).then(data => {
        if (data.Data.length > 1)
          throw new Error("Benutzereinstellungen ['FDRW_WF_User'] konnten nicht eindeutig bestimmt werden!");

        if (throwIfNoneAvailable === true && data.Data.length == 0) {
          throw new Error(`Im Mandanten "${navUrl.Mandant}" sind Sie nicht als RWF- Benutzer eingerichtet!`);

        }
        return data.Data.length == 1 ? data.Data[0] : undefined;
      }, err => {
        const str = "" + fn.resolveToError(err);
        if (str.match(/^'Benutzer (Einrichtung|Newsystem)' ist nicht vorhanden\. Identifizierende Felder und Werte: Benutzer-ID='.*'$/i)) {
          throw new Error(`Im Mandanten "${navUrl.Mandant}" sind Sie nicht als Benutzer eingerichtet!`);
        }
        throw err;
      });
    });
  }

  /** Load Own user Info or throw explicit error if not available*/
  private GetRWF3User(explicitNavUrl?: TNavUrlAccess, IsStandalone?: boolean, throwIfNonAvailable?: boolean) {
    return this.resolveNavUrl(explicitNavUrl, true).then(urlInfo => {
      return this.$c.get<IFDRW_Workflow_Users & {
        WindowsLogin: string,
      }>("/infoma.Web.KWF3/GetCurrentWorkflowUser?" + fn.EncodeQueryData({ navUrl: urlInfo.FullUrl }), { cache: true }).then(userInfo => {
        fn.resolveToError(userInfo && userInfo.data);
        const user = userInfo.data;
        if (user) {
          let msg = "Benutzer '" + user.WindowsLogin + "' ist in NewSystem ";
          if (user.UserLocked)
            throw new Error(msg + "gesperrt.");
          if (user.LoginDisabled && !(IsStandalone))
            throw new Error(msg + "deaktiviert.");
        }

        return this.$c.when(user || <any>this.LoadWorkflowUser(explicitNavUrl, throwIfNonAvailable)).then(s => {
          return user;
        });
      });
    });
  }

  /** Load RWF Aware Companies for current User */
  public LoadCompanyInfo(explicitNavUrl?: TNavUrlAccess, fullThrow?: boolean) {
    return this.resolveNavUrl(explicitNavUrl, false).then(navUrl => {
      var getRWFCompanies = this.LoadRwfAwareCompanyInfo(navUrl);
      // merge with user accesible Companies
      return this.NavClient.GetCompanies(navUrl).then(companies => {
        return getRWFCompanies.then(rwfCompanies => {
          var validatedCompanies = rwfCompanies.filter(item => item.Setup && companies.indexOf(item.Company) >= 0).map(item => item.Company);
          if (validatedCompanies.length > 0)
            return validatedCompanies;

          return companies;
        }, err => {
          if (fullThrow === true) {
            throw err;
          }
          return companies;
        });
      });
    });
  }

  LoadRwfAwareCompanyInfo(navUrl: INavUrlInfo) {
    var rwfCUrl = "/infoma.Web.KWF3/GetRWFCompanyInfo?" + fn.EncodeQueryData({ navUrl: navUrl.FullUrl });
    return this.$c.get<{ Company: string, Setup: IFDRW_Workflow_Setup_WS, Error?: string }[]>(rwfCUrl, { cache: true }).then(d => d.data);
  }

  /** Load Workflow Users from backend for given url, silently ignore failure if not explicitly requested otherwise */
  LoadWorkflowUsers(explicitNavUrl?: TNavUrlAccess, throwOnFaiure?: boolean, forceRefresh?: boolean) {
    return this.resolveNavUrl(explicitNavUrl).then(navUrl => {
      return this.$c.get<IFDRW_Workflow_Users[]>("/infoma.Web.KWF3/GetWorkflowUsers?" +
        fn.EncodeQueryData({ navUrl: navUrl.FullUrl, forceRefresh: !!forceRefresh })).then(d => d.data || [], err => {
          if (throwOnFaiure === true) {
            throw err;
          }
          // In case of Error : silently Continue
          return <IFDRW_Workflow_Users[]>[];
        });
    });
  }

  /** Initialize RWF Bootstrap information via "/infoma.Web.KWF/Settings"
   * with explicit company set or a "full" url.
   * UI- Iinteracitve selection is disabled, error thrown if company not available
   * a 'baseurl' wOut explicit company will trigger UserInteraction to choose Company
  */
  public BootstrapRwf(explicitNavUrl?: TNavUrlAccess, explicitCompany?: string): PromiseLike<IRwfBootstrapParameters> {
    const $c = this.$c;
    return this.LoadRwfSettings(explicitNavUrl, explicitCompany).then(settings => {
      let urlInfo = ResolveNavUrl(settings.NavUrl, true);

      this.bulkEnsurePageClients(urlInfo);
      // controls order of rejections
      const wfSetup = this.LoadWorkflowSetup(urlInfo);
      return wfSetup.then(() => {
        let result: TRecOptPromise<IRwfBootstrapParameters> = {
          NavUrlInfo: ResolveNavUrl(settings.NavUrl),
          RwfSetup: wfSetup,
          RwfUser: this.LoadWorkflowUser(urlInfo, true),
          RwfSettings: settings,
          InfomaClient: AsyncInfomaClientIntegration()
        };
        return $c.all(result).then((result) => {
          const RwfUser = result.RwfUser;
          // not nice, todo: remove User info from Settings .,.
          result.RwfSettings.User.DisplayName = RwfUser.UserName || RwfUser.ID;
          return result;
        });
      });
    });
  }

  /** Load Rwf Settings .... */
  LoadRwfSettings(explicitNavUrl: TNavUrlAccess = undefined, explicitCompany?: string, impLogin: boolean = true): PromiseLike<IRwfSettings> {
    const $c = this.$c;
    // We might have neither an explicit nor implicit Url (from Client side)
    if (!this.NavUrl.ExternalDefaultUrl && !explicitNavUrl) {
      var resolveUrl = this.$c.when<INavUrlInfo>(undefined);
    } else {
      resolveUrl = this.resolveNavUrl(explicitNavUrl, false);
    }

    return resolveUrl.then(/** might be null! */_navUrlInfo => {
      const qData = _navUrlInfo ? fn.EncodeQueryData({ navUrl: _navUrlInfo.FullUrl, company: _navUrlInfo.Mandant }) : "";
      return $c.get<IRwfSettings | IRWFLogonRequest>("/infoma.Web.KWF/Settings?" + qData).then(data => {
        const settings = data.data;
        function IsLogonRequest(d: any): d is IRWFLogonRequest {
          return d && (<IRWFLogonRequest>d).UserLogonRequired === true;
        }

        if (IsLogonRequest(settings)) {
          if (settings.Authorization === "InfomaOnline") {
            RedirectToInfomaOnlineLogin();
            throw fn.error.UICancel("Signin Necessary");
          }
          if (!impLogin) {
            throw new Error('Authorisierung notwendig!');
          }

          if (!_navUrlInfo) {
            //MSQQQ
            throw new Error("Für die interaktive Authorisierung ist ein Mandant erforderlich!");
          }
          return this.userinteraction.ModalLogin(loginData =>
            $c.post<string[]>("/infoma.Web.KWF/UserLogin", {
              EncodedLogonData: fn.toBase64(loginData.Username + ":" + loginData.Password),
              NavUrl: _navUrlInfo.FullUrl
            }).then((data) => data.data)).then(() => this.LoadRwfSettings(_navUrlInfo, explicitCompany, false));
        } else {
          let urlInfo = ResolveNavUrl(settings.NavUrl, false);
          explicitCompany = explicitCompany || (_navUrlInfo && _navUrlInfo.Mandant) || urlInfo.Mandant;
          let availableCompanies: string[] = [];
          const loadRwfCompanies = this.LoadCompanyInfo(urlInfo.FullUrl);
          const validateexplicitCompany = (explicitCompany) => {
            validateCompany(explicitCompany, availableCompanies);
          };
          let loadandCheckCompanyIfexplicit = loadRwfCompanies.then(companies => {
            availableCompanies = companies;
            return validateexplicitCompany(explicitCompany);
          }, fn.noop);

          const company = $c.when(loadandCheckCompanyIfexplicit).then(() => {
            if (explicitCompany) {
              return explicitCompany;
            }
            return loadRwfCompanies.then(availableCompanies => {
              if (availableCompanies.length == 1) {
                // For Single Available Company UserInteraction not necessary
                return availableCompanies[0];
              } else {
                // Use Stored Default Access if configured
                const storedDefault = DefaultCompanyAccess.get(urlInfo);
                if (storedDefault && availableCompanies.indexOf(storedDefault) >= 0) {
                  return storedDefault;
                }
              }
              return this.userinteraction.ModalSelectCompany(availableCompanies, (info: ValidateCompanyConfig) =>
                this.GetRWF3User(BuildNavUrl(urlInfo.FullUrl, info.Company), settings.User && settings.User.IsStandalone, true).then(rwfUser => {
                  if (info.StoreAsDefault) {
                    DefaultCompanyAccess.set(urlInfo, info.Company);
                  }
                }));
            });
          });

          return company.then(resolvedCompany => {
            validateexplicitCompany(resolvedCompany);
            urlInfo = ResolveNavUrl(BuildNavUrl(settings.NavUrl, resolvedCompany), true);
            settings.NavUrlInfo = urlInfo;
            settings.NavUrl = urlInfo.FullUrl;
            settings.CurrentCompany = urlInfo.Mandant;
            return settings;
          });
        }
      }, err => {
        // Settings Download Failed
        let explicitUrlInfo: INavUrlInfo;
        var resolveNavUrl = $c.when().then(() => explicitNavUrl && ResolveNavUrl(explicitNavUrl, false));
        return resolveNavUrl.then(e => explicitUrlInfo = e, fn.noop).then(() => {
          const explicitInfo = explicitUrlInfo ? ` für '${explicitUrlInfo.FullUrl}'` : ``;
          throw new Error(`Anwendungsintialisierung${explicitInfo} fehlgeschlagen:\n` + fn.resolveToError(err));
        });
      });
    });
  }

  private get userinteraction() { return this.$c.resolve("Rwf")("RwfUserInteraction"); }

  /** Current Navision Url */
  private resolveNavUrl(explicitNavUrl?: TNavUrlAccess, explictValidateCompany?: boolean | string | undefined) {
    let validateCompany: boolean;
    if (explictValidateCompany && (typeof explictValidateCompany === "string")) {
      validateCompany = false;
      var customCompany = explictValidateCompany;
    } else {
      validateCompany = typeof explictValidateCompany == "boolean" ? explictValidateCompany : true;
    }

    return this.NavUrl.ResolveUrl(explicitNavUrl || this.explicitNavUrl, validateCompany).then(info => {
      if (customCompany) {
        return ResolveNavUrl(BuildNavUrl(info.FullUrl, customCompany), true);
      } else {
        return info;
      }
    });
  }

  /** Ensure bulk pageClient resolution calls */
  private bulkEnsurePageClients(urlInfo: INavUrlInfo) {
    const NavClient = this.NavClient;
    if (!(NavClient && urlInfo && urlInfo.FullUrl)) {
      return;
    }

    NavClient.GetPageClientInfos(<TDefinedPageName[]>[
      "FDRW_Workflow_Setup_WS",
      "FDRW_Workflow_Running_Instances",
      "FDRW_WFInstance",
      "FDRW_Workflow_User_Instances_List"
    ], urlInfo.FullUrl, false);
    NavClient.GetPageClientInfos(<TDefinedPageName[]>[
      "FDRW_CustomCaption",
      "FDRW_WF_User",
      "FDRW_Workflow_Step_List",
      "FDRW_Workflow_Instance_Action_List",
      "FDRW_WF_Action_Fwd_Comments_List"
    ], urlInfo.FullUrl, true);

  }
}

/** Local Storage Indirection; Problem : "In den Microsoft Interneterweiterungen ist ein Fehler aufgetreten", [???] */
var _localStorage = (function () {
  var l = fn.tryProxy(function () { return window.localStorage; }, window)() || { getItem: <(s: string) => string>fn.noop, setItem: <(s: string, d: string) => string>fn.noop, removeItem: <(s: string) => void>fn.noop };
  return {
    /** getItem */
    getItem: fn.tryProxy(l.getItem, l),

    /** set item */
    setItem: fn.tryProxy(l.setItem, l),

    /** set item */
    removeItem: fn.tryProxy(l.removeItem, l),
  };
})();

const KWF_CurrentMandant = "KWF_CurrentMandant";
export const DefaultCompanyAccess = {
  get: (info: INavUrlInfo) => _localStorage.getItem(KWF_CurrentMandant),
  set: (info: INavUrlInfo, company: string) => company ? _localStorage.setItem(KWF_CurrentMandant, company) : _localStorage.removeItem(KWF_CurrentMandant),
};


/** Validate given company available, throw user error message */
function validateCompany(company: string, companies: string[]) {
  if ((companies || []).length > 0 && company && companies.indexOf(company) < 0) {
    throw new Error(`Der Mandant ${company} ist nicht vorhanden bzw. Sie sind für diesen nicht berechtigt.`);
  }
}

/** Record of Items or Promises of Items */
export type TRecOptPromise<T> = {
  readonly [P in keyof T]: T[P] | PromiseLike<T[P]>;
};

/** Rwf Services for AutoResolution */
export type RwfAutoResolver = IAutoResolvable<IRwfInjectable>;

/** Auto Resolve Names in RWF Context */
export interface IRwfInjectable {
  "$c": CompatibilityService;
  "RwfBackend": RwfBackend;
  "RwfBootstrap": RwfBootstrap;
  "RwfInvoiceData": RwfInvoiceDataService;
  "FwfDataService": FwfDataService,
  "RwfUserInteraction": RwfUserInteraction;
  "RwfAppSettings": RwfAppSettings;
  "RwfApplication": RwfApplicationService;
}

/** result of '/infoma.Web.KWF/Settings?' */
export interface IRwfSettings {
  /** Navision URL */
  NavUrl: string;

  /** Default Company from Server if given */
  DefaultCompany?: string;

  /** Mandant (Set On Client After Load!) */
  CurrentCompany: string;

  /** Resolve Current Info (resolved on Client after Load) */
  NavUrlInfo: INavUrlInfo;

  /** Default Nav Url Config from server Info */
  DefaultNavUrl?: INavUrlInfo;

  /** Name of the Configuration in FormsServer (see NavCustomization.xml)*/
  ConfigName: string;

  /** Information about Current User (from AD/Environment on server) */
  User: {
    /** Display Name for the User */
    DisplayName: string;
    /** (Login) Name of  User (in RWF3 : gets overwritten with NAV UserName from FDRW_WF_User)*/
    UserName: string
    /** Marker : User Is KWF Admin */
    IsAdmin: boolean,
    /** Windows Login Name */
    LoginName: string
    /** Is not an Actual Nav User (='Setra' User) */
    IsStandalone: boolean
  };

  /** Current Standalone State */
  StandaloneInfo?: string;

  /**Version Compatibility : Allow to supress Features depending on avalability of certain pages */
  CheckPages: { [key in "FDRW_Workflow_Start"
    | "FDRW_CustomCaption"
    | "FDRW_Deferral_Schedule"
    | "FDRW_Job_Queue_Entry_List"
    | "FDRW_PurchaseOrder"
    | "FDRW_RecentlyPostedInvoice_List"
    | "FDRW_WF_Instance_Subscriptions"
    | "SmtpMail"
    /** Removed wtih 19.1 must be called before,. bot not with or after ...*/
    | "FillInstanceActions"
    ]: boolean };

  /** Sign Configuration via Gears */
  Gears?: {
    Url: string;
    Configuration?: string;
  };
  /** Is PRoGOV Signature Functionality Available  */
  ProGOV?: boolean,

  /** Is Infoma Smart Client Available  */
  InfomaSmartClientAvailable?: boolean;

  ReauthenticateSpanInSeconds?: number;

  readonly Authentication?: TAuthorization
}

type TAuthorization = "Default" | "NavUserPassword" | "ExplicitAuth" | "InfomaOnline";

/** Load of RWf Setttings might lead to Login Request */
interface IRWFLogonRequest {
  UserLogonRequired: true;
  Authorization?: TAuthorization;
}
